import {browserGetBrowserWidth,
        browserAfterRendering,
        BROWSER_SMALL_SCREEN,
        browserIsSmallScreen
        } from "./browser.js";
import { cacheGetCacheTime } from "./cache.js";
import { pagePxSizeToInt } from "./page.js";
import { POSTER_MINIMUM_WIDTH } from "./poster.js";
import CONFIG from "./../config.json";

const POSTERS_BIG_SCREENS_MARGIN = 66; // 16*2 + 16*2 + 1*2
const POSTERS_SMALL_SCREENS_MARGIN = 36; // 16*2 + 1*2 + 1*2
const POSTERS_SCREEN_BOUNDARY = 640;
const POSTERS_FETCH_NUMBER = 20;
const POSTERS_IMAGE_MAXIMUM_SIZE = 512;

const _posters = []; // caches, cursors could imply posters repetitions
let _postersEntries = {}; // avoiding posters repetitions in the same page
let _postersIndex = 0;
let _postersAbsoluteIndex = 0;
let _postersToReturn = 0;
let _postersElementWrapper;
const _postersToResolve = [];
let _postersBatchNumber = 0;
let _postersAllDownloaded = false;
let _postersFetching = false;
let _postersReject;
let _postersFilter = "";
let _postersFilterTown = "";
let _postersFilterRegion = "";
let _postersFilterPublisherId = "";
const _postersCursor = [];

function postersReset() {
    _posters.length = 0;
    _postersEntries = {};
    _postersIndex = 0;
    _postersAbsoluteIndex = 0;
    _postersToReturn = 0;
    _postersToResolve.length = 0;
    _postersBatchNumber = 0;
    _postersAllDownloaded = false;
    _postersFetching = false;
    _postersFilter = "";
    _postersFilterTown = "";
    _postersFilterRegion = "";
    _postersFilterPublisherId = "";
    _postersCursor.length = 0;
}

function postersResetResolve() {
    _postersToReturn = 0;
    _postersToResolve.length = 0;
}

function postersGetMargin() {
    if (browserGetBrowserWidth() <= POSTERS_SCREEN_BOUNDARY) {
        return POSTERS_SMALL_SCREENS_MARGIN;
    } else {
        return POSTERS_BIG_SCREENS_MARGIN;
    }
}

function getPostersMaximumImageWidth(naturalWidth, naturalHeight) {
    if ((naturalWidth > POSTERS_IMAGE_MAXIMUM_SIZE) && (naturalWidth >= naturalHeight)) {
        return POSTERS_IMAGE_MAXIMUM_SIZE;
    } else if (naturalHeight > POSTERS_IMAGE_MAXIMUM_SIZE) {
        return (POSTERS_IMAGE_MAXIMUM_SIZE * naturalWidth) / naturalHeight;
    }
    return naturalWidth;
}

function postersSetPosterResposiveWidth(
        poster,
        width,
        margin,
        checkTitleOverflow = true
        ) {

    if ((poster.id) && (poster.id === "poster-main-poster"))
    {
        if (browserIsSmallScreen() === false)
            return;
    }

    poster.style.width = postersGetPosterResposiveWidth(width, margin) + "px";

    // poster title is overflowing after rendering?
    if (checkTitleOverflow === true) {
        postersCheckTitleOverflowAfterRendering(poster, margin);
}
}

function postersCheckTitleOverflowAfterRendering(poster, margin) {
    browserAfterRendering(() => {
        const titleWidth = postersGetPosterTitleWidth(poster);
        if (pagePxSizeToInt(poster.style.width) < titleWidth) {
            poster.style.width =
                    postersGetPosterResposiveWidth(titleWidth, margin) + "px";
        }
    });
}

function postersGetPosterResposiveWidth(width, margin) {
    if (typeof margin === "undefined") {
        margin = postersGetMargin();
    }

    if (width < POSTER_MINIMUM_WIDTH) {
        width = POSTER_MINIMUM_WIDTH;
    }

    const browserWidth = browserGetBrowserWidth();
    const totalWidthWithMargin = width + margin;

    if (
            totalWidthWithMargin <= browserWidth &&
            browserWidth > BROWSER_SMALL_SCREEN
            ) {
        return width;
    } else {
        return browserWidth - margin;
    }
}

function postersGetPosterTitleWidth(poster) {
    const posterTitle = poster.getElementsByClassName("posterTitle");
    if (posterTitle.length === 0) {
        return 0;
    }
    return posterTitle[0].offsetWidth;
}

function postersExcludePoster(posterPublisherId, posterId) {
    if (!_postersEntries[posterPublisherId]) {
        _postersEntries[posterPublisherId] = [];
    }

    if (!_postersEntries[posterPublisherId].includes(posterId)) {
        _postersEntries[posterPublisherId].push(posterId);
    }
}

function postersIsExcluded(posterPublisherId, posterId) {
    if (_postersEntries[posterPublisherId]) {
        return _postersEntries[posterPublisherId].includes(posterId);
    }
    return false;
}

function postersGetYear(date) {
    const parts = date.split("/");
    return parts[2];
}

function postersGetDateString(date, time) {
    const parts = date.split("/");
    const ddate = new Date(parts[2], parts[1] - 1, parts[0]);
    const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
    };
    const dateString = ddate.toLocaleDateString("ca-ES", options);
    return dateString.charAt(0).toUpperCase() + dateString.slice(1);
}

function postersGetTimeString(time) {
    let dateString = "";
    if (time !== "") {
        dateString = "a les " + time;
    }
    return dateString;
}

function postersGetEndDateOrTime(beginDate, endDate, time) {
    let dateString = "";
    if (beginDate !== endDate) {
        dateString = "- " + postersGetDateString(endDate);
    } else {
        dateString = postersGetTimeString(time);
    }
    return dateString;
}

function postersReplaceApostrophesInText(str) {
    return str.split("'").join("&apos;");
}

function postersEscapeSpacesAndApostrophes(str) {
    return str.split(" ").join("-").split("'").join("_");
}

function postersUnescapeSpacesAndApostrophes(str) {
    return str.split("-").join(" ").split("_").join("'");
}

function _postersFetch(createAndAppend) {
    //
    while (_postersToReturn > 0) {
        if (_postersIndex >= _posters.length) {
            if (_postersAllDownloaded === false) {
                _postersFetchFromServer(createAndAppend);
            } else {
                while (_postersToReturn > 0) {
                    const resolve = _postersToResolve.shift();
                    resolve({index: null, poster: null, wrapper: null, image: null});
                    _postersToReturn--;
                }
            }
            break;
        }

        // appending poster to the dom -----------------------------------------
        const currentPoster = _posters[_postersIndex];
        if (
                postersIsExcluded(currentPoster.publisherId, currentPoster.id) ===
                false &&
                (_postersFilter === "" ||
                        currentPoster.labelsList.includes(_postersFilter)) &&
                (_postersFilterTown === "" ||
                        currentPoster.town === _postersFilterTown) &&
                (_postersFilterRegion === "" ||
                        currentPoster.region === _postersFilterRegion) &&
                (_postersFilterPublisherId === "" ||
                        currentPoster.publisherId === _postersFilterPublisherId)
                ) {
            // avoiding repetitions due to batches incoherences-----------------
            postersExcludePoster(currentPoster.publisherId, currentPoster.id);

            // resolving promise
            const resolve = _postersToResolve.shift();

            // do not change resolve variable names !!!
            // backward compatibility !!!! TO BE REMOVED WHEN CC & LG & POST UPDATED!!!
            // and map.js too
            let wrapper;
            let image;
            if (createAndAppend !== null) {
                wrapper = createAndAppend(currentPoster, _postersElementWrapper);
                image = wrapper.getElementsByClassName("posterImage")[0];
            }
            const index = _postersAbsoluteIndex;
            const poster = currentPoster;
            resolve({index, poster, wrapper, image});

            //
            _postersToReturn--;
        }
        //
        _postersIndex++;
        _postersAbsoluteIndex++;
    }
}

function posterGetDayMonthPattern(separator) {
    const date = new Date();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return day + separator + month;
}

function postersSaveCursor(batchNumber, cursor) {
    if (batchNumber !== 0) {
        _postersCursor[batchNumber - 1] = cursor;
    }
}

function postersGetCursor(batchNumber) {
    if (batchNumber === 0) {
        return null;
    }
    return _postersCursor[batchNumber - 1];
}

function posterGetCursorByAbsoluteIndex(absoluteIndex) {
    const auxBatchNumber = Math.floor(absoluteIndex / POSTERS_FETCH_NUMBER);
    return postersGetCursor(auxBatchNumber);
}

// function postersSetCursorCookie(batchNumber, cursor)
// {
//    browserSetCookie("cursor-" + posterGetDayMonthPattern('-') + "-batch-" + batchNumber, cursor, 1);
// }

// function postersGetCursorCookie(batchNumber)
// {
//    return(browserGetCookie("cursor-" + posterGetDayMonthPattern('-') + "-batch-" + batchNumber));
// }

function _postersFetchFromServer(createAndAppend) {
    let _postersQueryLink = "?";
    _postersFetching = true;

    let resource =
            `${CONFIG.posters_api_url}/resource/poster/public/from/${posterGetDayMonthPattern("/")}`;

    if (_postersBatchNumber !== 0) {
        resource += "?cursor=" + postersGetCursor(_postersBatchNumber);
        _postersQueryLink = "&";
    }

    const cacheTime = cacheGetCacheTime();
    if (cacheTime !== null && cacheTime !== "") {
        resource += _postersQueryLink + "cacheTime=" + cacheTime;
    }

    fetch(resource)
            .then((response) => response.json())
            .then((aux) => {
                //
                _posters.push(...aux.list);
                _postersBatchNumber++;
                postersSaveCursor(_postersBatchNumber, aux.cursor);

                // is there more authors
                if (aux.list.length < POSTERS_FETCH_NUMBER) {
                    _postersAllDownloaded = true;
                }
                _postersFetching = false;
                //
                _postersFetch(createAndAppend);
            })
            .catch((err) => {
                _postersFetching = false;
                _postersReject(err);
            });
}

function postersFetchAPoster(createAndAppend, wrapper) {
    return new Promise(function (resolve, reject) {
        _postersToReturn++;
        _postersElementWrapper = wrapper;
        _postersToResolve.push(resolve);
        // it only saves one !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
        _postersReject = reject;

        if (_postersFetching === false) {
            _postersFetch(createAndAppend);
        }
    });
}

function postersIsThereMorePosters() {
    if (_postersAllDownloaded === true) {
        if (_postersIndex >= _posters.length) {
            return false;
        }
    }
    return true;
}

function postersGetPostersIndex() {
    return _postersAbsoluteIndex;
}

function postersSetPostersIndex(index) {
    postersSetPostersIndexAndCursor(index);
}

function postersSetPostersIndexAndCursor(index, cursor = "") {
    // server index
    _postersAbsoluteIndex = index;
    // local array index
    _postersIndex = index % POSTERS_FETCH_NUMBER;
    // offset where to star to fecth in the server
    _postersBatchNumber = Math.floor(index / POSTERS_FETCH_NUMBER);
    // batch cursor
    postersSaveCursor(_postersBatchNumber, cursor);
}

// function posterIsThereCursorForTheIndex(index)
// {
//
//    if (index >= POSTERS_FETCH_NUMBER)
//    {
//        if (postersGetCursorCookie(Math.floor(index / POSTERS_FETCH_NUMBER)) === "")
//        {
//            return(false);
//        }
//    }
//
//    return(true);
// }

function postersSetPostersFilter(filter) {
    _postersFilter = filter;
}

function postersSetRegionTownFilter(region, town) {
    _postersFilterRegion = region;
    _postersFilterTown = town;
}

function postersSetPublisherFilter(publisherId) {
    _postersFilterPublisherId = publisherId;
}

export {
postersReset,
        postersSetPublisherFilter,
        postersSetRegionTownFilter,
        postersSetPostersFilter,
        postersSetPostersIndex,
        postersGetPostersIndex,
        postersIsThereMorePosters,
        postersFetchAPoster,
        posterGetCursorByAbsoluteIndex,
        postersUnescapeSpacesAndApostrophes,
        postersGetYear,
        postersSetPosterResposiveWidth,
        getPostersMaximumImageWidth,
        postersResetResolve,
        postersGetEndDateOrTime,
        postersEscapeSpacesAndApostrophes,
        postersReplaceApostrophesInText,
        postersExcludePoster,
        postersGetDateString,
        postersCheckTitleOverflowAfterRendering,
        postersGetMargin,
        postersGetPosterResposiveWidth,
        postersSetPostersIndexAndCursor
        };
